import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const EXPAND_CHANGES_PARAM = "expandChanges";
const HIGHLIGHT_ROW_PARAM = "highlightRow";
const EXPAND_NOTES_PARAM = "expandNotes";

export function aroundIdQueryVariable(
  aroundId: string | undefined,
  paginationVariables: { after?: string; before?: string; first?: number; last?: number }
) {
  return paginationVariables.after || paginationVariables.before ? undefined : aroundId;
}

export function aroundIdMissingFromLoadedPage(
  aroundId: string | undefined,
  options: { loading: boolean; loadedNodeIds?: string[] }
) {
  if (!aroundId || options.loading || !options.loadedNodeIds) {
    return false;
  }

  return !options.loadedNodeIds.includes(aroundId);
}

function deepLinkTargetFromSearchParams(searchParams: URLSearchParams) {
  return (
    searchParams.get(EXPAND_CHANGES_PARAM) ||
    searchParams.get(HIGHLIGHT_ROW_PARAM) ||
    searchParams.get(EXPAND_NOTES_PARAM) ||
    undefined
  );
}

interface UseChangeRequestAroundIdOptions {
  resetKey?: string;
}

export function useChangeRequestAroundId({ resetKey }: UseChangeRequestAroundIdOptions = {}) {
  const [searchParams] = useSearchParams();
  const [aroundId, setAroundId] = useState<string | undefined>(() => deepLinkTargetFromSearchParams(searchParams));
  const previousResetKey = useRef(resetKey);
  const previousTarget = useRef(deepLinkTargetFromSearchParams(searchParams));

  useEffect(() => {
    const nextAroundId = deepLinkTargetFromSearchParams(searchParams);

    if (previousResetKey.current !== resetKey) {
      previousResetKey.current = resetKey;
      previousTarget.current = nextAroundId;
      setAroundId(undefined);
      return;
    }

    if (nextAroundId && nextAroundId !== previousTarget.current) {
      setAroundId(nextAroundId);
    }
    previousTarget.current = nextAroundId;
  }, [resetKey, searchParams]);

  const clearAroundId = useCallback(() => {
    setAroundId(undefined);
  }, []);

  return { aroundId, clearAroundId };
}

interface UseChangeRequestListDeepLinkOptions {
  loading: boolean;
  rowIds: string[];
  serverCurrentPage?: number;
  onTargetMissing?: () => void;
}

export function useChangeRequestListDeepLink({
  loading,
  rowIds,
  serverCurrentPage,
  onTargetMissing
}: UseChangeRequestListDeepLinkOptions) {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [expandChangesRowId, setExpandChangesRowId] = useState<string | null>(null);
  const [highlightRowId, setHighlightRowId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrolledHighlightIdRef = useRef<string | null>(null);
  const expandChanges = searchParams.get(EXPAND_CHANGES_PARAM);
  const highlightRow = searchParams.get(HIGHLIGHT_ROW_PARAM);
  const expandNotes = searchParams.get(EXPAND_NOTES_PARAM);
  const targetId = expandChanges || highlightRow || expandNotes;
  const hasTargetRow = !targetId || rowIds.includes(targetId);

  useEffect(() => {
    if (!expandChanges && !highlightRow && !expandNotes) {
      return;
    }

    if (loading || serverCurrentPage === undefined) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete(EXPAND_CHANGES_PARAM);
    next.delete(HIGHLIGHT_ROW_PARAM);
    if (serverCurrentPage > 0) {
      next.set("currentPage", String(serverCurrentPage));
    } else {
      next.delete("currentPage");
    }
    const nextSearch = next.toString();
    if (nextSearch !== searchParams.toString()) {
      navigate({ pathname, search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    }

    let expandId: string | null = null;
    let highlightId: string | null = null;

    if (expandChanges && hasTargetRow) {
      expandId = expandChanges;
      highlightId = expandChanges;
    } else if (highlightRow && rowIds.includes(highlightRow)) {
      highlightId = highlightRow;
    } else if (expandNotes && rowIds.includes(expandNotes)) {
      highlightId = expandNotes;
    } else {
      onTargetMissing?.();
    }

    setExpandChangesRowId(expandId);
    setHighlightRowId(highlightId);
  }, [
    expandChanges,
    expandNotes,
    hasTargetRow,
    highlightRow,
    loading,
    navigate,
    onTargetMissing,
    pathname,
    rowIds,
    searchParams,
    serverCurrentPage
  ]);

  useEffect(() => {
    if (!highlightRowId) {
      scrolledHighlightIdRef.current = null;
      return;
    }

    if (scrolledHighlightIdRef.current === highlightRowId) {
      return;
    }

    let cancelled = false;
    let attempt = 0;
    let timeoutId: number | undefined;
    let rafId: number | undefined;

    const tryScroll = () => {
      if (cancelled) {
        return;
      }

      const row = containerRef.current?.querySelector("tr.row-highlight") as HTMLElement | null;
      if (row) {
        scrolledHighlightIdRef.current = highlightRowId;
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      if (attempt++ >= 30) {
        return;
      }

      rafId = requestAnimationFrame(tryScroll);
    };

    timeoutId = window.setTimeout(tryScroll, 0);

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [highlightRowId]);

  return { expandChangesRowId, highlightRowId, containerRef };
}
